// Kanban kolonları için önceden tanımlı renk paletleri
export const PREDEFINED_COLORS = {
    answerStatus: {
        answered: '#10b981',
        unanswered: '#ef4444',
    },
    lastConversationStatus: {
        active: '#3b82f6',
        chatbot: '#8b5cf6',
        new: '#f59e0b',
        lost: '#6b7280',
        destroyed: '#dc2626',
        private: '#ec4899',
    },
    blockedSnapshot: {
        true: '#dc2626',
        false: '#10b981',
    },
    platform: {
        supsis: '#3b82f6',
        email: '#10b981',
        whatsapp: '#22c55e',
        web: '#6366f1',
        mobile: '#8b5cf6',
        instagram: '#ec4899',
        telegram: '#0ea5e9',
        messenger: '#3b82f6',
        'meta-leads': '#3b82f6',
        trendyol: '#f97316',
        linkedin: '#0077b5',
        google: '#ef4444',
        calendar: '#10b981',
    },
    metaLeadStatus: {
        new_lead: '#3b82f6',
        initial_contact: '#06b6d4',
        needs_assessment: '#8b5cf6',
        proposal_sent: '#a855f7',
        proposal_review: '#d946ef',
        negotiation: '#ec4899',
        decision_stage: '#f43f5e',
        closure: '#10b981',
        follow_up: '#f59e0b',
    },
    lastBroadcastStatus: {
        read: '#10b981',
        sent: '#3b82f6',
        delivered: '#06b6d4',
        deleted: '#ef4444',
        edited: '#f59e0b',
        failed: '#dc2626',
    },
    lastTriggerType: {
        api: '#8b5cf6',
        broadcast: '#3b82f6',
        automation: '#10b981',
        null: '#6b7280',
    },
    preferredLang: {
        tr: '#ef4444',
        en: '#3b82f6',
        de: '#f59e0b',
        fr: '#8b5cf6',
        es: '#ec4899',
        ar: '#10b981',
        ru: '#dc2626',
    },
};

/**
 * HSL bazlı otomatik renk paleti üretir
 * @param {number} count - Kaç renk üretileceği
 * @returns {string[]} Hex renk dizisi
 */
export function generateColorPalette(count) {
    if (count <= 0) return [];
    
    const colors = [];
    const goldenRatioConjugate = 0.618033988749895;
    
    // Başlangıç hue değeri (rasgele ama tutarlı)
    let hue = 0.5;
    
    // Sabitlenmiş saturation ve lightness değerleri
    const saturation = 65;
    const lightness = 55;
    
    for (let i = 0; i < count; i++) {
        // Golden ratio ile dengeli dağıtılmış hue değerleri
        hue = hue + goldenRatioConjugate;
        hue = hue % 1;
        
        const color = hslToHex(hue * 360, saturation, lightness);
        colors.push(color);
    }
    
    return colors;
}

/**
 * HSL'den Hex'e dönüştürme
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {string} Hex color
 */
function hslToHex(h, s, l) {
    s = s / 100;
    l = l / 100;
    
    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c / 2;
    
    let r = 0, g = 0, b = 0;
    
    if (0 <= h && h < 60) {
        r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
        r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
        r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
        r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
        r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
        r = c; g = 0; b = x;
    }
    
    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);
    
    const toHex = (val) => {
        const hex = val.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Bir grup field ve value için renk döndürür
 * @param {string} field - Grup field adı
 * @param {string|boolean} value - Grup değeri
 * @param {number} index - Bu değerin indeksi
 * @param {number} total - Toplam değer sayısı
 * @returns {string} Hex renk
 */
export function getColorForGroup(field, value, index, total) {
    // Önceden tanımlı renk varsa onu kullan
    if (PREDEFINED_COLORS[field]) {
        const predefinedColor = PREDEFINED_COLORS[field][value];
        if (predefinedColor) {
            return predefinedColor;
        }
    }
    
    // Boolean değerler için özel renk
    if (typeof value === 'boolean' || value === 'true' || value === 'false') {
        return value === true || value === 'true' ? '#10b981' : '#ef4444';
    }
    
    // Boş değer için gri
    if (!value || value === 'null' || value === 'undefined') {
        return '#6b7280';
    }
    
    // Otomatik renk üret
    const palette = generateColorPalette(total);
    return palette[index % palette.length] || '#6b7280';
}

/**
 * Hex rengi RGB'ye çevirir
 * @param {string} hex - Hex renk (#RRGGBB formatında)
 * @returns {object} {r, g, b} objesi
 */
export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : { r: 107, g: 114, b: 128 }; // Default gray
}

/**
 * Renk tonuna göre text rengini belirler (siyah veya beyaz)
 * @param {string} hexColor - Hex renk
 * @returns {string} '#ffffff' veya '#000000'
 */
export function getContrastTextColor(hexColor) {
    const rgb = hexToRgb(hexColor);
    const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
    return brightness > 155 ? '#000000' : '#ffffff';
}

